<template>
  <div class="container mt-4">

    <!-- TOP BAR -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">{{ $t('loanManage.title') }}</h4>

      <!-- Toujours visible, désactivé si pas admin -->
     <button
  class="btn btn-sm text-white"
  style="background:#b03a45;"
  :disabled="!isAdmin"
  @click="goToMyLoans"
  title="Go to my loan requests"
>
  💳 {{ $t('loanManage.myLoans') }}  
</button>

    </div>


    <div class="card shadow border-0">
      <div
        class="card-header text-white fw-bold"
        style="background: linear-gradient(195deg, #4b5563, #8d2e38);"
      >
        <i class="material-icons me-2">description</i>
        {{ $t('loanManage.subtitle') }}
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="text-uppercase small" style="background-color: #f8f9fa;">
            <tr>
              <th>{{ $t('loanManage.table.employee') }}</th>
              <th>{{ $t('loanManage.table.amount') }}</th>
              <th>{{ $t('loanManage.table.type') }}</th>
              <th>{{ $t('loanManage.table.reason') }}</th>
              <th>{{ $t('loanManage.table.status') }}</th>
              <th>{{ $t('loanManage.table.supervisor') }}</th>
              <th>{{ $t('loanManage.table.actions') }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="loan in loans" :key="loan.id">
              <td>
                <div class="fw-semibold">{{ loan.user?.firstname }} {{ loan.user?.lastname }}</div>
                <div class="text-muted small">{{ loan.user?.email || '' }}</div>
              </td>

              <td>{{ loan.amount }} TND</td>

              <td>
                <span class="badge bg-pink text-white">{{ loan.loan_type }}</span>
              </td>

              <td>{{ loan.reason || '—' }}</td>

              <td>
                <span :class="['badge', statusClass(loan.status)]">{{ loan.status }}</span>
              </td>

              <td>
                <div v-if="loan.supervisor">
                  {{ loan.supervisor.firstname }} {{ loan.supervisor.lastname }}
                </div>
                <div v-else class="text-muted">—</div>
              </td>

              <td>
                <button
                  v-if="loan.status === 'Pending'"
                  class="btn btn-success btn-sm me-2"
                  @click="approveLoan(loan.id)"
                >
                  ✔ {{ $t('loanManage.actions.approve') }}
                </button>

                <button
                  v-if="loan.status === 'Pending'"
                  class="btn btn-danger btn-sm me-2"
                  @click="rejectLoan(loan.id)"
                >
                  ✘ {{ $t('loanManage.actions.reject') }}
                </button>

                <!-- Monthly Payment Button for Approved Loans -->
                <button
                  v-if="loan.status === 'Approved'"
                  class="btn text-white btn-sm me-2"
                  :style="{ backgroundColor: '#8d2e38' }"
                  @click="openPaymentModal(loan)"
                  title="Add Monthly Payment"
                >
                  💰 {{ $t('loanManage.actions.monthlyPayment') }}
                </button>

                <!-- Payment History Button -->
                <button
                  v-if="loan.status === 'Approved' && loan.payments && loan.payments.length > 0"
                  class="btn btn-secondary btn-sm"
                  @click="showPaymentHistory(loan)"
                  title="View Payment History"
                >
                  📊 {{ $t('loanManage.actions.paymentHistory') }}
                </button>
              </td>
            </tr>

            <tr v-if="!loading && loans.length === 0">
              <td colspan="7" class="text-center text-muted py-4">
                {{ $t('loanManage.noRequests') }}
              </td>
            </tr>

            <tr v-if="loading">
              <td colspan="7" class="text-center text-muted py-4">
                <i class="fas fa-spinner fa-spin me-2"></i> {{ $t('loanManage.loading') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>

    <!-- Monthly Payment Modal -->
    <div v-if="showPaymentModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              💰 {{ $t('loanManage.paymentModal.title') }}
            </h5>
            <button type="button" class="btn-close" @click="closePaymentModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{ $t('loanManage.paymentModal.employee') }}</label>
              <input type="text" class="form-control" :value="selectedLoan?.user?.firstname + ' ' + selectedLoan?.user?.lastname || 'Employee'" readonly>
            </div>
            <div class="mb-3">
              <label for="amount" class="form-label">{{ $t('loanManage.paymentModal.amount') }} *</label>
              <input 
                type="number" 
                id="amount"
                class="form-control" 
                v-model.number="paymentForm.amount"
                min="1"
                step="0.01"
                :placeholder="$t('loanManage.paymentModal.amountPlaceholder')"
                required
              >
            </div>
            <div class="mb-3">
              <label for="payment_date" class="form-label">{{ $t('loanManage.paymentModal.paymentDate') }} *</label>
              <input 
                type="date" 
                id="payment_date"
                class="form-control" 
                v-model="paymentForm.payment_date"
                :max="today"
                required
              >
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closePaymentModal">
              {{ $t('loanManage.paymentModal.cancel') }}
            </button>
            <button 
              type="button" 
              class="btn text-white" 
              :style="{ backgroundColor: '#8d2e38' }"
              @click="submitMonthlyPayment"
              :disabled="submittingPayment"
            >
              <i v-if="submittingPayment" class="fas fa-spinner fa-spin me-1"></i>
              {{ submittingPayment ? $t('loanManage.paymentModal.submitting') : $t('loanManage.paymentModal.submit') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment History Modal -->
    <div v-if="showHistoryModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              📊 {{ $t('loanManage.historyModal.title') }}
            </h5>
            <button type="button" class="btn-close" @click="closeHistoryModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="paymentHistory.length === 0" class="text-center text-muted py-3">
              {{ $t('loanManage.historyModal.noPayments') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{{ $t('loanManage.historyModal.date') }}</th>
                    <th>{{ $t('loanManage.historyModal.amount') }}</th>
                    <th>{{ $t('loanManage.historyModal.remaining') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payment in paymentHistory" :key="payment.id">
                    <td>{{ formatDate(payment.payment_date) }}</td>
                    <td>{{ payment.amount }} TND</td>
                    <td>{{ payment.remaining_after_payment || payment.remaining_amount || 0 }} TND</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeHistoryModal">
              {{ $t('loanManage.historyModal.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Employee Payment History Modal -->
    <div v-if="showEmployeeHistoryModal" class="modal fade show d-block" style="background-color: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              📋 {{ $t('loanManage.employeeHistoryModal.title') }}
            </h5>
            <button type="button" class="btn-close" @click="closeEmployeeHistoryModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <strong>{{ $t('loanManage.employeeHistoryModal.loanAmount') }}:</strong> 
              {{ selectedEmployeeLoan?.total_amount }} TND
            </div>
            <div class="mb-3">
              <strong>{{ $t('loanManage.employeeHistoryModal.remainingAmount') }}:</strong> 
              {{ selectedEmployeeLoan?.remaining_amount }} TND
            </div>
            <div v-if="employeePaymentHistory.length === 0" class="text-center text-muted py-3">
              {{ $t('loanManage.employeeHistoryModal.noPayments') }}
            </div>
            <div v-else class="table-responsive">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{{ $t('loanManage.employeeHistoryModal.paymentDate') }}</th>
                    <th>{{ $t('loanManage.employeeHistoryModal.amountPaid') }}</th>
                    <th>{{ $t('loanManage.employeeHistoryModal.remainingAfter') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payment in employeePaymentHistory" :key="payment.payment_date">
                    <td>{{ formatDate(payment.payment_date) }}</td>
                    <td>{{ payment.amount_paid }} TND</td>
                    <td>{{ payment.remaining_after_payment }} TND</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeEmployeeHistoryModal">
              {{ $t('loanManage.employeeHistoryModal.close') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

/* =============================
   STATE
============================= */
const loans = ref([])
const loading = ref(true)

const showPaymentModal = ref(false)
const showHistoryModal = ref(false)
const showEmployeeHistoryModal = ref(false)

const selectedLoan = ref(null)
const selectedEmployeeLoan = ref(null)

const paymentHistory = ref([])
const employeePaymentHistory = ref([])

const submittingPayment = ref(false)

const today = new Date().toISOString().split('T')[0]

const paymentForm = ref({
  amount: '',
  payment_date: today
})

/* =============================
   ADMIN DETECTION
============================= */
const user = JSON.parse(localStorage.getItem('user') || '{}')

const rolesNormalized = computed(() => {
  const r = user?.roles

  if (Array.isArray(r) && typeof r[0] === 'string')
    return r.map(x => String(x).toLowerCase())

  if (Array.isArray(r) && typeof r[0] === 'object')
    return r.map(x => String(x?.name || x?.role || '').toLowerCase()).filter(Boolean)

  if (typeof user?.role === 'string')
    return [user.role.toLowerCase()]

  return []
})

const isAdmin = computed(() => {
  return (
    rolesNormalized.value.includes('admin') ||
    rolesNormalized.value.includes('superadmin') ||
    user?.is_admin === true ||
    user?.isAdmin === true
  )
})

const goToMyLoans = () => {
  if (!isAdmin.value) return
  router.push('/employee/loans/request')
}

/* =============================
   FETCH LOANS
============================= */
const fetchLoans = async () => {
  loading.value = true
  try {
    let res
    if (isAdmin.value) {
      res = await axios.get('/employee/loans')
      loans.value = res.data.loans || []
    } else {
      res = await axios.get('/employee/loans/me')
      loans.value = res.data || []
    }
  } catch {
    toast.error('Failed to load loans')
  } finally {
    loading.value = false
  }
}

/* =============================
   STATUS BADGE
============================= */
const statusClass = (status) => {
  switch (status) {
    case 'Approved': return 'bg-success text-white'
    case 'Rejected': return 'bg-danger text-white'
    case 'Pending': return 'bg-warning text-white'
    case 'Completed': return 'bg-primary text-white'
    default: return 'bg-secondary text-white'
  }
}

/* =============================
   APPROVE / REJECT
============================= */
const approveLoan = async (id) => {
  try {
    await axios.patch(`/employee/loans/approve/${id}`)
    toast.success('Loan approved successfully')
    await fetchLoans()
  } catch {
    toast.error('Failed to approve loan')
  }
}

const rejectLoan = async (id) => {
  try {
    await axios.patch(`/employee/loans/reject/${id}`)
    toast.success('Loan rejected successfully')
    await fetchLoans()
  } catch {
    toast.error('Failed to reject loan')
  }
}

/* =============================
   CALCULATE REMAINING
============================= */
const calculateRemaining = (loan) => {
  const totalPaid = loan.payments
    ? loan.payments.reduce((sum, p) => sum + parseFloat(p.amount), 0)
    : 0

  return parseFloat(loan.amount) - totalPaid
}

/* =============================
   OPEN PAYMENT MODAL
============================= */
const openPaymentModal = (loan) => {
  selectedLoan.value = {
    ...loan,
    remaining_amount: calculateRemaining(loan)
  }

  paymentForm.value = {
    amount: '',
    payment_date: today
  }

  showPaymentModal.value = true
}

const closePaymentModal = () => {
  showPaymentModal.value = false
  selectedLoan.value = null
}

/* =============================
   SUBMIT PAYMENT
============================= */
const submitMonthlyPayment = async () => {
  if (!paymentForm.value.amount || paymentForm.value.amount <= 0) {
    toast.error('Payment amount is required')
    return
  }

  const remaining = selectedLoan.value.remaining_amount
  const amount = parseFloat(paymentForm.value.amount)

  if (amount > remaining) {
    toast.error(`Payment exceeds remaining amount (${remaining} TND)`)
    return
  }

  submittingPayment.value = true

  try {
    const res = await axios.post(
      `/employee/loans/${selectedLoan.value.id}/payment`,
      paymentForm.value
    )

    toast.success(res.data.message)

    closePaymentModal()
    await fetchLoans()

  } catch (e) {
    toast.error(e.response?.data?.message || 'Payment failed')
  } finally {
    submittingPayment.value = false
  }
}

/* =============================
   PAYMENT HISTORY (ADMIN)
============================= */
const showPaymentHistory = (loan) => {

  let remaining = parseFloat(loan.amount)

  paymentHistory.value = loan.payments
    ? loan.payments
        .sort((a,b)=>new Date(a.payment_date)-new Date(b.payment_date))
        .map(p => {
          remaining -= parseFloat(p.amount)
          return {
            ...p,
            remaining_after_payment: Math.max(0, remaining)
          }
        })
    : []

  showHistoryModal.value = true
}

const closeHistoryModal = () => {
  showHistoryModal.value = false
  paymentHistory.value = []
}

/* =============================
   EMPLOYEE HISTORY
============================= */
const showEmployeePaymentHistory = (loan) => {
  selectedEmployeeLoan.value = loan
  employeePaymentHistory.value = loan.payments_history || []
  showEmployeeHistoryModal.value = true
}

const closeEmployeeHistoryModal = () => {
  showEmployeeHistoryModal.value = false
  selectedEmployeeLoan.value = null
  employeePaymentHistory.value = []
}

/* =============================
   FORMAT DATE
============================= */
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR')
}

/* =============================
   INIT
============================= */
onMounted(fetchLoans)
</script>



<style scoped>
.badge {
  padding: 0.4em 0.8em;
  border-radius: 0.5rem;
  text-transform: uppercase;
  font-size: 0.75rem;
}
.bg-pink {
  background-color: #d63384;
}
</style>
